import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { HighlightVariant } from '../../../../../components/FileViewer/pdf-viewer/highlight-layer/HighlightLayer.props';
import { CitationReferenceData } from '../../../../../services/reference-generator/types';

export type CitationContextType = {
  activeCitation?: CitationReferenceData;
  /** Highlight color for the active citation; 'ai' (AI-purple) while it is pending review. */
  activeCitationVariant: HighlightVariant;
  /**
   * Bumped on EVERY setActiveCitation call - including re-activating the citation
   * that is already active. Scroll effects keyed on it re-fire when the user clicks
   * the same eyeball again after scrolling away (a same-reference setState alone
   * would bail out and never re-run them).
   */
  activeCitationNonce: number;
  /**
   * Set (or clear) the active citation. `variant` controls the PDF highlight color;
   * callers derive it from the CITATION'S review status (statusToHighlightVariant,
   * SCAL-8986) - pending -> 'ai', anything else -> the default plain-yellow 'citation'.
   */
  setActiveCitation: (citation?: CitationReferenceData, variant?: HighlightVariant) => void;
}

const CitationContext = createContext<CitationContextType>({
  activeCitationVariant: 'citation',
  activeCitationNonce: 0,
  setActiveCitation: () => {},
});

export const useCitationContext = () => useContext(CitationContext);

const CitationProvider = ({ children }: { children: React.ReactNode }) => {
  // One state object: the citation, its variant, and a monotonically increasing
  // nonce. They always change together, and the nonce guarantees every activation
  // produces a NEW state even when the same citation object is set again.
  const [active, setActive] = useState<{
    citation?: CitationReferenceData;
    variant: HighlightVariant;
    nonce: number;
  }>({ variant: 'citation', nonce: 0 });

  const handleSetActiveCitation = useCallback(
    (citation?: CitationReferenceData, variant: HighlightVariant = 'citation') => {
      setActive(prev => ({ citation, variant, nonce: prev.nonce + 1 }));
    },
    []
  );

  const value = useMemo(
    () => ({
      activeCitation: active.citation,
      activeCitationVariant: active.variant,
      activeCitationNonce: active.nonce,
      setActiveCitation: handleSetActiveCitation,
    }),
    [active, handleSetActiveCitation]
  );

  return <CitationContext.Provider value={value}>{children}</CitationContext.Provider>;
};

export default CitationProvider;
